/* eslint-disable @typescript-eslint/no-shadow */
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ApiPaiValues } from '@upright/common/model/output/eu-regulation/resolved-pais/types';
import { Bounds, CarouselLayout, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { nullBounds, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { getPaiNaturesForImpactCategory } from '@upright/ui/common/upright/core/impact-lenses/pais/common/utils';

interface PaiLensLayout {
  impactPaiCountBoxArea: (index: number, side: Side, impact: ImpactCategoryId) => Bounds;
  impactPaiCountBoxesArea: (side: Side, impact: ImpactCategoryId) => Bounds;
}

export function paiLensLayout(base: CarouselLayout, data: ApiPaiValues | undefined): PaiLensLayout {
  /**
   * Area for symbol indicating whether company has
   * set a target within this category
   */
  function impactPaiCountBoxArea(index: number, side: Side, impact: ImpactCategoryId) {
    const da = base.impactDataArea(side, impact);
    const horizontalMargin = 10;
    const height = da.height - 4;
    const spacing = 6;
    const fullWidth = base.fullArea().width;

    function getBoxWidth() {
      if (fullWidth > 485) {
        return 52;
      } else {
        return 25;
      }
    }

    const boxWidth = getBoxWidth();

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = da.x1 + horizontalMargin + (spacing + boxWidth) * index;
        const x2 = x1 + boxWidth;
        return { x1, x2 };
      } else {
        const x2 = da.x2 - horizontalMargin - (spacing + boxWidth) * index;
        const x1 = x2 - boxWidth;
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    const y1 = da.y1 + (da.height - height) / 2;
    const y2 = y1 + height;
    return toBounds({
      x1,
      x2,
      y1,
      y2,
    });
  }

  function impactPaiCountBoxesArea(side: Side, impact: ImpactCategoryId) {
    const pais = data;
    if (!pais) {
      return nullBounds;
    }
    const firstArea = impactPaiCountBoxArea(0, side, impact);
    const natures = getPaiNaturesForImpactCategory(impact, pais);
    const count = Object.keys(natures).length;
    const lastArea = impactPaiCountBoxArea(count - 1, side, impact);

    const x1 = Math.min(firstArea.x1, lastArea.x1);
    const x2 = Math.max(firstArea.x2, lastArea.x2);
    return toBounds({
      x1,
      x2,
      y1: firstArea.y1,
      y2: firstArea.y2,
    });
  }

  return {
    impactPaiCountBoxArea,
    impactPaiCountBoxesArea,
  };
}
